import { memo, ReactNode } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { TabIndex, TabProps } from "../types";
import TabLayout from "../TabLayout";
import usePhoneVerification from "./usePhoneVerification";
import { formatDate } from "util/formaters";
import Tabs from "ui/atoms/Tabs";
import CodeSnippet from "ui/molecules/CodeSnippet";

const FALLBACK = ' - ';

const formatValue = (value: string | number | null | undefined) =>
    (value === null || value === undefined || value === '') ? FALLBACK : String(value);

const formatDateTime = (value: string | null | undefined) =>
    value ? formatDate(value, { pattern: 'DD/MM/YYYY HH:mm' }) : FALLBACK;

const PhoneVerification = (props: TabProps<TabIndex.PhoneVerification>) => {
    const {
        getTabPayload,
        tabRegistry: [phoneVerification],
        dialogRequestProcessing: [isLoading]
    } = props;

    usePhoneVerification(props);

    const general: Array<[ReactNode, string]> = [
        ['Phone', formatValue(phoneVerification?.phone)],
        ['Phone verified', phoneVerification?.phoneVerified ? 'Yes' : 'No']
    ];

    const succeeded: Array<[ReactNode, string]> = [
        ['Phone', formatValue(phoneVerification?.verificationPhone)],
        ['Type', formatValue(phoneVerification?.verificationPhoneType)],
        ['Increment', formatValue(phoneVerification?.verificationIncrement)],
        ['Created at', formatDateTime(phoneVerification?.verificationCreatedAt)],
        ['Updated at', formatDateTime(phoneVerification?.verificationUpdatedAt)]
    ];

    const failed: Array<[ReactNode, string]> = [
        ['Phone', formatValue(phoneVerification?.failedVerificationPhone)],
        ['Increment', formatValue(phoneVerification?.failedVerificationIncrement)],
        ['Created at', formatDateTime(phoneVerification?.failedVerificationCreatedAt)],
        ['Updated at', formatDateTime(phoneVerification?.failedVerificationUpdatedAt)]
    ];

    const verificationResponse =
        phoneVerification?.verificationPhoneResponse ??
        phoneVerification?.verificationResponse ??
        null;

    const failedVerificationResponse =
        phoneVerification?.failedVerificationPhoneResponse ??
        phoneVerification?.failedVerificationResponse ??
        null;

    const renderRows = (rows: Array<[ReactNode, string]>) =>
        rows.map(([label, value]) => (
            <Box
                key={String(label)}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(120px, max-content) 1fr',
                    columnGap: 2,
                    mb: 2
                }}
            >
                <Box
                    sx={{
                        color: 'text.disabled',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {label}:
                </Box>
                <Box
                    sx={{
                        fontWeight: 600,
                        wordBreak: 'break-word'
                    }}
                >
                    {isLoading ? <Skeleton /> : value}
                </Box>
            </Box>
        ));

    return (
        <TabLayout
            getTabPayload={getTabPayload}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    columnGap: 3
                }}
            >
                <Box>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 2 }}
                    >
                        Phone
                    </Typography>
                    {renderRows(general)}
                </Box>
                <Box>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 2 }}
                    >
                        Successful verification
                    </Typography>
                    {renderRows(succeeded)}
                </Box>
                <Box>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 2 }}
                    >
                        Failed verification
                    </Typography>
                    {renderRows(failed)}
                </Box>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Tabs
                model={[
                    {
                        key: 'verificationPhoneResponse',
                        label: 'Verification Response',
                        component: (
                            <CodeSnippet
                                data={verificationResponse}
                                isLoading={isLoading}
                                sx={{ maxHeight: 400 }}
                            />
                        )
                    },
                    {
                        key: 'failedVerificationPhoneResponse',
                        label: 'Failed Verification Response',
                        component: (
                            <CodeSnippet
                                data={failedVerificationResponse}
                                isLoading={isLoading}
                                sx={{ maxHeight: 400 }}
                            />
                        )
                    },
                    ...([
                        ['phoneVerificationsFromPhoneHistories', 'Phone Verifications From Phone Histories'],
                        ['phoneVerificationsFromTransactions', 'Phone Verifications From Transactions'],
                        ['failedPhoneVerificationsFromPhoneHistories', 'Failed Phone Verifications From Phone Histories'],
                        ['failedPhoneVerificationsFromTransactions', 'Failed Phone Verifications From Transactions'],
                        ['phoneHistories', 'Phone Histories'],
                        ['waMessagesFromPhone', 'WA Messages From Phone'],
                        ['waMessagesFromPhoneHistories', 'WA Messages From Phone Histories'],
                        ['waMessagesFromTransactions', 'WA Messages From Transactions']
                    ] as const).map(([key, label]) => ({
                        key,
                        label,
                        component: (
                            <CodeSnippet
                                data={phoneVerification?.[key] ?? null}
                                isLoading={isLoading}
                                sx={{ maxHeight: 400 }}
                            />
                        )
                    }))
                ]}
                TabListContainerProps={{
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
                TabListProps={{
                    'aria-label': 'phone verification responses',
                    variant: 'scrollable',
                    scrollButtons: 'auto'
                }}
            />
        </TabLayout>
    );
};

export default memo(PhoneVerification);
